/**
 * Small dense linear algebra for the Fay-Herriot core (brief 9.7).
 *
 * Matrices are row-major number[][]. The engine only needs p-by-p systems
 * (p = number of covariates), so clarity beats speed here. Solves go
 * through the Cholesky factorization, as the brief requires, rather than
 * explicit inversion wherever a solve is enough; the one explicit inverse
 * (of the p-by-p matrix M) is needed for standard errors and g2 terms and
 * is computed from the same factorization.
 */
export function zeros(rows, cols) {
    return Array.from({ length: rows }, () => new Array(cols).fill(0));
}
export function identity(n) {
    const out = zeros(n, n);
    for (let i = 0; i < n; i++)
        out[i][i] = 1;
    return out;
}
export function transpose(a) {
    const rows = a.length;
    const cols = rows === 0 ? 0 : a[0].length;
    const out = zeros(cols, rows);
    for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++)
            out[j][i] = a[i][j];
    }
    return out;
}
export function matMul(a, b) {
    const rows = a.length;
    const inner = b.length;
    const cols = inner === 0 ? 0 : b[0].length;
    const out = zeros(rows, cols);
    for (let i = 0; i < rows; i++) {
        for (let k = 0; k < inner; k++) {
            const aik = a[i][k];
            if (aik === 0)
                continue;
            for (let j = 0; j < cols; j++)
                out[i][j] += aik * b[k][j];
        }
    }
    return out;
}
export function matVec(a, v) {
    return a.map((row) => {
        let sum = 0;
        for (let j = 0; j < v.length; j++)
            sum += row[j] * v[j];
        return sum;
    });
}
export function dot(a, b) {
    let sum = 0;
    for (let i = 0; i < a.length; i++)
        sum += a[i] * b[i];
    return sum;
}
/** trace(a * b) without forming the product: sum_ij a[i][j] * b[j][i]. */
export function traceOfProduct(a, b) {
    const n = a.length;
    let sum = 0;
    for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++)
            sum += a[i][j] * b[j][i];
    }
    return sum;
}
/**
 * Lower Cholesky factor L of a symmetric positive definite matrix
 * (a = L L'). Returns null when a is not numerically positive definite
 * (a pivot is zero, negative, or non-finite) — the caller treats that as
 * a singular design.
 */
export function cholesky(a) {
    const n = a.length;
    const L = zeros(n, n);
    for (let i = 0; i < n; i++) {
        for (let j = 0; j <= i; j++) {
            let sum = a[i][j];
            for (let k = 0; k < j; k++)
                sum -= L[i][k] * L[j][k];
            if (i === j) {
                if (!(sum > 0) || !Number.isFinite(sum))
                    return null;
                L[i][i] = Math.sqrt(sum);
            }
            else {
                L[i][j] = sum / L[j][j];
            }
        }
    }
    return L;
}
/** Solve A x = b for x, given the lower Cholesky factor of A. */
export function cholSolveVec(L, b) {
    const n = L.length;
    const y = new Array(n).fill(0);
    for (let i = 0; i < n; i++) {
        let sum = b[i];
        for (let k = 0; k < i; k++)
            sum -= L[i][k] * y[k];
        y[i] = sum / L[i][i];
    }
    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
        let sum = y[i];
        for (let k = i + 1; k < n; k++)
            sum -= L[k][i] * x[k];
        x[i] = sum / L[i][i];
    }
    return x;
}
/** Inverse of A from its lower Cholesky factor, symmetrized. */
export function cholInverse(L) {
    const n = L.length;
    const inv = zeros(n, n);
    for (let col = 0; col < n; col++) {
        const e = new Array(n).fill(0);
        e[col] = 1;
        const x = cholSolveVec(L, e);
        for (let row = 0; row < n; row++)
            inv[row][col] = x[row];
    }
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const avg = 0.5 * (inv[i][j] + inv[j][i]);
            inv[i][j] = avg;
            inv[j][i] = avg;
        }
    }
    return inv;
}
/** Factor a and return its inverse, or null when not positive definite. */
export function inverseSpd(a) {
    const L = cholesky(a);
    return L === null ? null : cholInverse(L);
}
//# sourceMappingURL=linalg.js.map